import React, { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
const AddDiagnose = () => {
  const [diagnosisName, setDiagnosisName] = useState("");
  const [symptom, setSymptom] = useState("");
  const [symptoms, setSymptoms] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  const navigate = useNavigate();
  const { diagnosis_id } = useParams(); // get diagnosis_id from route params

  // Fetch diagnosis if editing
  useEffect(() => {
    // Fetch categories for selector
    const fetchCategories = async () => {
      try {
        const token = (localStorage.getItem('session_token') || '').trim();
        const response = await fetch(
          `https://newerp.awasthivitality.com/api/category/list${token ? `?token=${encodeURIComponent(token)}` : ''}`,
          {
            headers: {
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
              ...(token ? { 'X-Session-Token': token } : {}),
            }
          }
        );
        const data = await response.json();
        if (response.ok && data?.data) {
          setCategories(data.data);
        }
      } catch (e) {
        console.error('Failed to load categories', e);
      }
    };

    fetchCategories();

    const fetchDiagnosis = async () => {
      if (!diagnosis_id) return;

      try {
        const token = (localStorage.getItem('session_token') || '').trim();
        const response = await fetch(
          `https://newerp.awasthivitality.com/api/diagnosis/get/${diagnosis_id}${token ? `?token=${encodeURIComponent(token)}` : ''}`,
          {
            headers: {
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
              ...(token ? { 'X-Session-Token': token } : {}),
            }
          }
        );
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();

        if (data.success && data.data) {
          setDiagnosisName(data.data.name);
          setSymptoms(data.data.symptoms || []);
          if (data.data.diagnosis_categoryid) {
            setSelectedCategoryId(String(data.data.diagnosis_categoryid));
          }
          setIsEdit(true);
        } else {
          alert("Diagnosis not found");
          navigate("/diagnoses");
        }
      } catch (error) {
        console.error("Error fetching diagnosis:", error);
        alert("Failed to load diagnosis. Please try again.");
        navigate("/diagnoses");
      }
    };

    fetchDiagnosis();
  }, [diagnosis_id, navigate]);

  const addSymptom = () => {
    if (symptom.trim()) {
      setSymptoms([...symptoms, symptom.trim()]);
      setSymptom("");
    }
  };

  const removeSymptom = (index) => {
    const updated = [...symptoms];
    updated.splice(index, 1);
    setSymptoms(updated);
  };

  const handleSubmit = async () => {
    if (!diagnosisName || symptoms.length === 0) {
      // alert("Please enter diagnosis name and at least one symptom.");
      toast.error("Please enter diagnosis name and at least one symptom.");

      return;
    }

    if (!selectedCategoryId) {
      toast.error("Please select a category.");
      return;
    }

    setLoading(true);

    const apiUrl = isEdit
      ? `https://newerp.awasthivitality.com/api/diagnosis/update/${diagnosis_id}`
      : `https://newerp.awasthivitality.com/api/diagnosis/add`;

    const payload = {
      name: diagnosisName,
      symptoms: symptoms,
      diagnosis_categoryid: Number(selectedCategoryId),
    };

    try {
      const token = (localStorage.getItem('session_token') || '').trim();
      const urlWithToken = `${apiUrl}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      const response = await fetch(urlWithToken, {
        method: isEdit ? "PUT" : "POST", // PUT for edit, POST for new
        headers: {
          "Content-Type": "application/json",
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(token ? { 'X-Session-Token': token } : {}),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(data.message || 'Diagnosis saved');
        navigate("/diagnoses");
      } else {
        const msg = data?.message || `HTTP ${response.status}`;
        toast.error("Failed to submit diagnosis: " + msg);
      }

    } catch (error) {
      console.error("Error submitting diagnosis:", error);
      alert("Failed to submit diagnosis. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        padding: "2rem",
        background: "#f4f5f7",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div style={{ width: "100%", marginLeft: "230px" }}>
        {/* Card */}
        <div
          style={{
            background: "#fff",
            borderRadius: "6px",
            overflow: "hidden",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "rgb(0 195 225)",
              color: "white",
              padding: "12px 16px",
              fontWeight: "bold",
            }}
          >
            {isEdit ? "Edit Diagnose" : "Add Diagnose"}
          </div>

          <div style={{ padding: "16px" }}>
            {/* Diagnosis Name */}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: "bold" }}>Diagnosis Name</label>
              <input
                type="text"
                value={diagnosisName}
                onChange={(e) => setDiagnosisName(e.target.value)}
                placeholder="Enter Diagnosis Name"
                style={{
                  width: "100%",
                  padding: "8px",
                  marginTop: "6px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </div>

            {/* Category */}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: "bold" }}>Category</label>
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  marginTop: "6px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.category_id || cat.id} value={cat.category_id || cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Symptoms */}
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: "bold" }}>Sign & Symptoms</label>
              <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                <input
                  type="text"
                  value={symptom}
                  onChange={(e) => setSymptom(e.target.value)}
                  placeholder="Enter Sign & Symptoms"
                  style={{
                    flex: 1,
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                  }}
                />
                <button
                  onClick={addSymptom}
                  style={{
                    background: "rgb(0 195 225)",
                    border: "none",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Add More
                </button>
              </div>
            </div>

            {/* Symptom Details */}
            <div
              style={{
                marginTop: "1rem",
                border: "1px solid #ddd",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "rgb(0 195 225)",
                  color: "white",
                  padding: "10px",
                  fontWeight: "bold",
                }}
              >
                Sign & Symptom Details
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ borderBottom: "1px solid #ddd", padding: "8px" }}>S No</th>
                    <th style={{ borderBottom: "1px solid #ddd", padding: "8px" }}>Sign & Symptom</th>
                    <th style={{ borderBottom: "1px solid #ddd", padding: "8px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {symptoms.length === 0 ? (
                    <tr>
                      <td colSpan="3" style={{ textAlign: "center", padding: "12px", color: "#666" }}>
                        No symptoms added
                      </td>
                    </tr>
                  ) : (
                    symptoms.map((s, i) => (
                      <tr key={i}>
                        <td style={{ padding: "8px" }}>{i + 1}</td>
                        <td style={{ padding: "8px" }}>{s}</td>
                        <td style={{ padding: "8px" }}>
                          <button
                            onClick={() => removeSymptom(i)}
                            style={{
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                              color: "red",
                            }}
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
  <ToastContainer 
    position="top-right" 
    autoClose={3000} 
    hideProgressBar={false} 
    newestOnTop={false} 
    closeOnClick 
    rtl={false} 
    pauseOnFocusLoss 
    draggable 
    pauseOnHover 
  />
            {/* Submit Button */}
            <button
              style={{
                marginTop: "1rem",
                padding: "10px 20px",
                background: "rgb(0 195 225)",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Submitting..." : isEdit ? "Update Diagnose" : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDiagnose;
